import isEmpty from 'lodash/isEmpty.js'
import {createSelector} from 'reselect'

import {hasSettingsPermissionSelector} from '../../data/me.js'
import {formSelector, getState, setForm, updateForm} from '../../store.js'
import {DATA_IMPORT} from './DataImportLocationListener.js'
import createCSVFile, {readCSVFile} from '../../common/createCSVFile.js'
import delay from '../../common/delay.js'
import {showMessageToast} from '../Header/Toast/index.js'
import {createNewTask} from '../../data/tasks.js'
import apiverson from '../../common/apiverson.js'
import {loadFileInForm} from '../../common/file.js'
import api from '../../common/api.js'

export const IGNORE_REGEX = /^[_]/

export function dataImportSelector(state) {
  return state.ui.dataImport
}

export const saveableIndexesSelector = createSelector(
  (state) => dataImportSelector(state).data,
  (allData) =>
    allData.reduce((sum, data, index) => {
      if (!data.isSaved && !data.isDeleted && isEmpty(data.errors)) {
        sum.push(index)
      }

      return sum
    }, []),
)

export const canUseDataImportSelector = createSelector(
  hasSettingsPermissionSelector,
  (hasPermission) => hasPermission,
)

const DEFAULT_DATA_IMPORT = {
  files: null,
  columns: null,
  rows: null,
  columnImportList: null,
  errors: null,
  rowErrors: null,
  computeErrorsMarker: null,
  mappingErrors: null,
  history: null,
  isStartingTask: false,
  serverError: null,
}

export function setupDataImportForm() {
  return structuredClone(DEFAULT_DATA_IMPORT)
}

export function updateDataImport(updates) {
  updateForm(DATA_IMPORT, updates)
}

export function dataImport2Selector(state) {
  return formSelector(state, {formName: DATA_IMPORT}) || DEFAULT_DATA_IMPORT
}

export function clearDataImport() {
  setForm(DATA_IMPORT, setupDataImportForm())
}

export async function processFiles(columnOptions, files) {
  const data = await readCSVFile(files)

  const [columns, ...rows] = data

  const columnImportList = guessAtColumnImportList(columnOptions, columns)

  updateDataImport({files, columns, rows, columnImportList})
}

export function columnOptionsSelector(state, {columnOptions}) {
  return columnOptions.filter((option) => {
    if (!option.onlyIf || option.onlyIf(getState())) {
      return option
    }
  })
}

export function columnOptionsToHeaders(columnOptions) {
  return columnOptionsSelector(getState(), {columnOptions}).reduce(
    (prev, option) => {
      let header = option.display

      if (option.required) {
        header += ' *Req'
      }

      prev.push(header)

      return prev
    },
    [],
  )
}

export function simpleColumnOptionsToRowArray(columnOptions, rowObjects) {
  columnOptions = columnOptionsSelector(getState(), {columnOptions})
  const rows = []

  for (const obj of rowObjects) {
    rows.push(
      columnOptions.reduce((prev, option) => {
        prev.push(obj[option.value])

        return prev
      }, []),
    )
  }

  return rows
}

export function guessAtColumnImportList(columnOptions, columns) {
  function find(regex) {
    return columns.findIndex(
      (column) => !column.match(IGNORE_REGEX) && column.search(regex) !== -1,
    )
  }

  return columnOptions.reduce((prev, option) => {
    const index = find(option.columnMap)

    if (index !== -1) {
      prev[index] = option.value
    }

    return prev
  }, new Array(columns.length).fill(''))
}

export function computeErrors({
  state,
  rows,
  columnImportList,
  columnOptions,
  offset = 0,
  limit = undefined,
  errors = {},
  rowErrors = [],
}) {
  const optionsByValue = columnOptions.reduce((prev, option) => {
    prev[option.value] = option

    return prev
  }, {})

  for (let index = offset; index <= offset + limit; index++) {
    const row = rows[index]
    const payload = {}

    // reached the end of the rows
    if (!row) {
      break
    }

    if (row.length === 0 || (row.length === 1 && row[0] === '')) {
      // blank row
      continue
    }

    for (const [i, key] of columnImportList.entries()) {
      if (!key) {
        continue
      }

      payload[key] = row[i]
    }

    for (const [key, value] of Object.entries(payload)) {
      const option = optionsByValue[key]

      if (!option || !option.error) {
        continue
      }

      const outcome = option.error(state, {value, payload})

      if (outcome) {
        errors[key] = errors[key] || {messages: new Set(), count: 0, option}

        errors[key].messages.add(outcome)
        errors[key].count += 1

        rowErrors[index] ||= []
        rowErrors[index].push(outcome)
      }
    }
  }

  return errors
}

const BLOCK_SIZE = 1000
export async function debounceComputeErrors(columnOptions) {
  const state = getState()
  const {rows, columnImportList} = dataImport2Selector(state)
  const errors = {}
  const rowErrors = []
  let marker = Math.random()
  let earlyExit = false
  const mappingErrors = []

  for (const option of columnOptions) {
    if (option.required && option.missing) {
      mappingErrors.push(option.display + ' is unmapped and required')
    } else if (option.duplicated) {
      mappingErrors.push(option.display + ' is mapped more than once')
    }
  }

  updateDataImport({computeErrorsMarker: marker, mappingErrors})

  if (rows && columnImportList) {
    const blockCount = Math.ceil(rows.length / BLOCK_SIZE)

    for (let n = 0; n < blockCount; n++) {
      await computeErrors({
        state,
        rows,
        columnImportList,
        columnOptions,
        offset: n * BLOCK_SIZE,
        limit: BLOCK_SIZE,
        errors,
        rowErrors,
      })

      if (dataImport2Selector(getState()).computeErrorsMarker !== marker) {
        earlyExit = true
        break
      }

      updateDataImport({errors})

      await delay(0)
    }
  }

  if (!earlyExit) {
    updateDataImport({
      errors,
      rowErrors: rowErrors.filter((v) => v).length > 0 ? rowErrors : null,
      computeErrorsMarker: null,
    })
  }
}

export function downloadPreImportErrorCSV() {
  let {columns, rows, rowErrors, files} = dataImport2Selector(getState())

  if (!rowErrors) {
    return
  }

  let errorColumnIndex = columns.findIndex((column) => column === '_error')

  if (errorColumnIndex === -1) {
    columns = [...columns, '_error']
    errorColumnIndex = columns.length - 1
  }

  rows = rows.map((row, index) => {
    row = [...row]

    row[errorColumnIndex] = (rowErrors[index] || []).join(', ')

    return row
  })

  createCSVFile(columns, rows, files[0].name)
}

export async function startDataImportTask() {
  updateDataImport({isStartingTask: true, serverError: null})

  try {
    const {columnImportList, files} = dataImport2Selector(getState())
    const {current} = dataImportSelector(getState())

    const formData = loadFileInForm('file', files[0])

    const {
      json: {id: document_id},
    } = await apiverson.post('/document', formData)

    await createNewTask(current, {
      document_id,
      header: true,
      keys: columnImportList,
    })

    showMessageToast('Started Data Import Task')

    clearDataImport()
  } catch (err) {
    updateDataImport({
      isStartingTask: false,
      serverError: `Error starting import task: ${err.message || err.error_message}`,
    })
  }
}

export async function getImportHistory() {
  try {
    const {current: type} = dataImportSelector(getState())

    updateDataImport({serverError: null})

    const {json} = await api.get('/new_task', {
      table_key: 'import_task',
      type,
      limit: 100,
    })

    const history = new Map()
    for (const task of json.task) {
      const documentID = task.params.document_id
      const item = history.get(documentID) || {
        document_id: documentID,
        tasks: [],
      }

      item.tasks.push(task)

      history.set(documentID, item)
    }

    updateDataImport({history: [...history.values()]})
  } catch (err) {
    updateDataImport({serverError: err.message || err.error_message})
  }
}
